import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BanknoteArrowUpIcon,
    BodyLargeRegular,
    BodySmallRegular,
    FileTextIcon,
    H1Regular,
    PlusIcon,
    Private,
    RefreshCwIcon,
    ScrollView,
    TileButton,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useUserAuthorization } from '../../../auth/hooks/useUserAuthorization';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { canUserCreateBankAccount } from '../../../common/utils/bankingUtils';
import { selectedSpaceAtom } from '../../state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import { BankDisclosureFootnote } from '../Disclosures';
import LegacySpaceCard from './LegacySpaceCard';

interface SpacesOverviewProps {
    totalAvailableBalance: string;
    totalInterestEarned: string;
    bankAccountDetails: BankAccountDisplayDetail[];
}

const LegacySpacesOverview = ({
    totalAvailableBalance,
    totalInterestEarned,
    bankAccountDetails,
}: SpacesOverviewProps) => {
    const user = useAuthedUser();
    const { accessToRestrictedCapabilities } = useUserAuthorization();
    const setSelectedSpace = useSetAtom(selectedSpaceAtom);

    const { allowed } = canUserCreateBankAccount(user);

    const [enableCreateAccount, setEnableCreateAccount] = useState(
        allowed && accessToRestrictedCapabilities.openBankAccount,
    );

    useEffect(() => {
        setEnableCreateAccount(
            allowed && accessToRestrictedCapabilities.openBankAccount,
        );
    }, [accessToRestrictedCapabilities.openBankAccount]);

    useEffect(() => {
        setSelectedSpace(null);
    }, []);

    return (
        <ScrollView showsVerticalScrollIndicator={false} paddingHorizontal={16}>
            <YStack
                width="100%"
                maxWidth={700}
                alignSelf="center"
                $sm={{ paddingHorizontal: 40 }}
            >
                <YStack gap={4} marginVertical={24} alignItems="center">
                    <BodyLargeRegular>
                        {`Total savings in ${bankAccountDetails.length} space${bankAccountDetails.length > 1 ? 's' : ''}`}
                    </BodyLargeRegular>
                    <Private>
                        <H1Regular>{totalAvailableBalance}</H1Regular>
                    </Private>
                    <Private>
                        <BodySmallRegular color="$green1000">
                            +{totalInterestEarned} interest earned this month
                        </BodySmallRegular>
                    </Private>
                </YStack>

                <XStack
                    justifyContent="space-between"
                    gap={8}
                    marginBottom={16}
                    height={54}
                >
                    {enableCreateAccount && (
                        <TileButton
                            icon={PlusIcon}
                            text="New"
                            onPress={() => {
                                router.navigate('/banking/create-edit-space');
                            }}
                        />
                    )}
                    <TileButton
                        icon={BanknoteArrowUpIcon}
                        text="Transfer"
                        onPress={() => {
                            router.navigate('/banking/transfer');
                        }}
                    />
                    <TileButton
                        icon={RefreshCwIcon}
                        text="Recurring"
                        onPress={() => {
                            router.navigate(
                                '/banking/manage-recurring-transfers',
                            );
                        }}
                    />
                    <TileButton
                        icon={FileTextIcon}
                        text="Documents"
                        onPress={() => {
                            router.navigate('/banking/documents');
                        }}
                    />
                </XStack>

                <YStack gap={12}>
                    {bankAccountDetails.map((detail) => (
                        <LegacySpaceCard
                            key={detail.bankAccountId}
                            size="md"
                            bankAccountDetail={detail}
                            onPress={() => {
                                setSelectedSpace(detail);
                                router.navigate({
                                    pathname: '/banking/space-details',
                                    params: {
                                        syncteraAccountId:
                                            detail.syncteraAccountId,
                                    },
                                });
                            }}
                        />
                    ))}
                </YStack>
                <View marginTop={16} paddingBottom={48}>
                    <BankDisclosureFootnote />
                </View>
            </YStack>
        </ScrollView>
    );
};

export default LegacySpacesOverview;
